import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import { styled } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import type { ReactNode } from "react";
import React, { useCallback, useMemo, useState } from "react";
import { Field } from "react-final-form";
import { useTranslation } from "react-i18next";

import type {
  DraggableProvided,
  DraggableStateSnapshot,
} from "@hello-pangea/dnd";
import { Draggable } from "@hello-pangea/dnd";
import ClearIcon from "@mui/icons-material/Clear";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ContentPasteIcon from "@mui/icons-material/ContentPaste";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SettingsBackupRestoreIcon from "@mui/icons-material/SettingsBackupRestore";
import { Tooltip } from "@mui/material";
import type { FieldArrayRenderProps } from "react-final-form-arrays";
import type { ArrayFieldDefinition, FieldDefinition } from "./types";
import { ErrorMessage } from "../ErrorMessage";

interface ArrayFieldItemProps {
  name: string;
  index: number;
  item: any;
  fields: FieldArrayRenderProps<any, HTMLElement>["fields"];
  fieldName: string;
  subschema?: ArrayFieldDefinition["subschema"];
  spacing: number;
  allValues: Record<string, unknown>;
  getField: (
    fieldName: string,
    field: FieldDefinition,
    values: Record<string, unknown>,
    allValues: Record<string, unknown>
  ) => ReactNode;
  arrayProps: Omit<
    ArrayFieldDefinition,
    keyof {
      label: any;
      xs: any;
      sm: any;
      md: any;
      lg: any;
      xl: any;
      subschema: any;
      addText: any;
      removeText: any;
      restoreText: any;
      softDelete: any;
      getNewItem: any;
      disableAdd: any;
      disableRemove: any;
      disableReorder: any;
      hideAddButton: any;
      hideRemoveButton: any;
      collapsible: any;
      defaultCollapsed: any;
      getTitle: any;
    }
  >;
  removeText: string;
  restoreText: string;
  softDelete?: boolean;
  disableRemove: boolean;
  disableReorder: boolean;
  hideRemoveButton: boolean;
  collapsible: boolean;
  defaultCollapsed: boolean;
  itemTitle: string;
  onCopy?: (item: any) => void;
  onPaste?: (index: number) => void;
  showCopyPaste?: boolean;
  expandedIndex?: number | null;
  setExpandedIndex?: (index: number | null) => void;
  isNewlyAdded?: boolean;
  onExpandChange?: () => void;
  touched?: boolean;
  error?: string;
}

const PREFIX = "ArrayFieldItem";

const classes = {
  header: `${PREFIX}-header`,
  headerCollapsible: `${PREFIX}-headerCollapsible`,
  headerCollapsed: `${PREFIX}-headerCollapsed`,
  dragHandle: `${PREFIX}-dragHandle`,
  title: `${PREFIX}-title`,
  titleDeleted: `${PREFIX}-titleDeleted`,
  content: `${PREFIX}-content`,
};

const getItemStyle = (
  draggableStyle: React.CSSProperties
): React.CSSProperties => ({
  marginBottom: 8,
  ...draggableStyle,
});

const StyledPaper = styled(Paper, {
  shouldForwardProp: (prop) =>
    typeof prop !== "string" || !prop.startsWith("$"),
})<{
  $isDragging: boolean;
  $isDeleted: boolean;
}>(({ theme, $isDragging, $isDeleted }) => ({
  marginBottom: 8,
  opacity: $isDeleted ? 0.5 : 1,
  border: $isDragging ? "2px solid #1976d2" : "none",

  [`& .${classes.header}`]: {
    display: "flex",
    alignItems: "center",
    padding: "12px 16px",
    backgroundColor: theme.palette.action.hover,
    borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
  },

  [`& .${classes.headerCollapsible}`]: {
    cursor: "pointer",
    "&:hover": {
      backgroundColor: theme.palette.action.selected,
    },
  },

  [`& .${classes.headerCollapsed}`]: {
    borderBottom: "none",
  },

  [`& .${classes.dragHandle}`]: {
    display: "flex",
    alignItems: "center",
    marginRight: 12,
    cursor: "grab",
  },

  [`& .${classes.title}`]: {
    flex: 1,
    fontWeight: "medium",
  },

  [`& .${classes.titleDeleted}`]: {
    fontWeight: "normal",
  },

  [`& .${classes.content}`]: {
    padding: "16px",
  },
}));

export const ArrayFieldItem: React.FC<ArrayFieldItemProps> = ({
  name,
  index,
  item,
  fields,
  fieldName,
  subschema,
  spacing,
  allValues,
  getField,
  arrayProps,
  removeText,
  restoreText,
  softDelete,
  disableRemove,
  disableReorder,
  hideRemoveButton,
  collapsible,
  defaultCollapsed,
  itemTitle,
  expandedIndex,
  setExpandedIndex,
  onCopy,
  onPaste,
  showCopyPaste,
  isNewlyAdded = false,
  onExpandChange,
  touched,
  error,
}) => {
  const { t } = useTranslation();

  // Определяем, используется ли accordion mode
  const isAccordionMode =
    expandedIndex !== undefined && setExpandedIndex !== undefined;

  // Локальное состояние для обычного режима, или управление через родительский компонент для accordion
  // Новые элементы должны быть открыты по умолчанию
  const [localIsCollapsed, setLocalIsCollapsed] = useState(
    isNewlyAdded ? false : defaultCollapsed
  );

  const isCollapsed = isAccordionMode
    ? expandedIndex !== index
    : localIsCollapsed;

  const toggleCollapse = useCallback(() => {
    if (isAccordionMode) {
      // В accordion mode переключаем между текущим индексом и null
      if (expandedIndex === index) {
        setExpandedIndex(null);
      } else {
        setExpandedIndex(index);
      }
    } else {
      // Обычный режим - переключаем локальное состояние
      setLocalIsCollapsed((prev) => !prev);
    }

    // Вызываем callback для удаления из списка новых элементов
    if (onExpandChange) {
      onExpandChange();
    }
  }, [isAccordionMode, expandedIndex, index, setExpandedIndex, onExpandChange]);

  // Определяем, нужно ли подсвечивать заголовок (для всех открытых элементов)
  const isExpanded = !isCollapsed;
  const shouldHighlight = isExpanded;

  // Function to find required fields in subschema (including nested arrays)
  const getRequiredFields = useCallback(() => {
    if (!subschema) return [];

    const requiredFields: {
      path: string;
      field: any;
      fullPath: string;
    }[] = [];

    const checkRequiredFieldsRecursively = (
      schema: Record<string, FieldDefinition>,
      currentPath = "",
      currentValues: any = fields.value[index]
    ) => {
      Object.keys(schema).forEach((fieldName) => {
        const field = schema[fieldName];
        const fieldPath = currentPath
          ? `${currentPath}.${fieldName}`
          : fieldName;
        const fullFieldPath = `${name}.${fieldPath}`;

        // Check visibility
        const isVisible = field.isVisible
          ? field.isVisible({
              values: currentValues,
              allValues,
            })
          : true;
        const hasVisibleCheck = field.visibleCheck
          ? field.visibleCheck({
              values: currentValues,
              allValues,
            })
          : true;

        if (isVisible && hasVisibleCheck) {
          // Check if this field is required
          const isRequired = (field as any).required === true;
          if (isRequired) {
            requiredFields.push({
              path: fieldPath,
              field: field,
              fullPath: fullFieldPath,
            });
          }

          // If this is an array field, recursively check its subschema
          if ((field as any).type === "array" && (field as any).subschema) {
            const arrayField = field as any;
            const arrayValues = currentValues?.[fieldName] || [];

            // Check required fields in array subschema for each item
            arrayValues.forEach((arrayItem: any, arrayIndex: number) => {
              const arrayItemPath = `${fieldPath}[${arrayIndex}]`;
              checkRequiredFieldsRecursively(
                arrayField.subschema,
                arrayItemPath,
                arrayItem
              );
            });
          }
        }
      });
    };

    checkRequiredFieldsRecursively(subschema);
    return requiredFields;
  }, [subschema, fields.value, index, allValues, name]);

  const { arrayItemFields, fieldNode, hiddenValidationFields } = useMemo(() => {
    let arrayItemFields: ReactNode;
    let fieldNode: ReactNode;
    let hiddenValidationFields: ReactNode = null;

    if (subschema) {
      arrayItemFields = Object.keys(subschema)
        .filter((subName) => {
          return subschema[subName].isVisible
            ? subschema[subName].isVisible({
                values: fields.value[index],
                allValues,
              })
            : true;
        })
        .map((subName) =>
          getField(
            `${name}.${subName}`,
            {
              ...subschema[subName],
            },
            fields.value[index],
            allValues
          )
        );

      // Create hidden validation fields for required fields when collapsed
      if (collapsible && isCollapsed) {
        const requiredFields = getRequiredFields();

        if (requiredFields.length > 0) {
          hiddenValidationFields = (
            <div
              style={{
                position: "absolute",
                width: "30px",
                height: "30px",
                overflow: "hidden",
                right: "60px",
                opacity: "0",
              }}
            >
              {requiredFields.map((requiredField) => {
                const fieldType = (requiredField.field as any).type;

                // Create a minimal hidden field for validation
                return (
                  <Field
                    key={`${requiredField.fullPath}_validation`}
                    name={requiredField.fullPath}
                    render={({ input }) => (
                      <input
                        name={input.name}
                        value={input.value || ""}
                        onChange={input.onChange}
                        type={
                          fieldType === "email"
                            ? "email"
                            : fieldType === "url"
                              ? "url"
                              : "text"
                        }
                        required={(requiredField.field as any).required}
                        style={{
                          position: "absolute",
                          width: "1px",
                          height: "1px",
                          padding: 0,
                          margin: 0,
                          overflow: "hidden",
                          clip: "rect(0, 0, 0, 0)",
                          whiteSpace: "nowrap",
                          border: 0,
                        }}
                      />
                    )}
                  />
                );
              })}
            </div>
          );
        }
      }
    } else {
      fieldNode = getField(
        fieldName,
        {
          type: arrayProps.subType as string,
          xs: 12,
          fullWidth: arrayProps.fullWidth,
          name: fieldName,
        } as FieldDefinition,
        fields.value[index],
        allValues
      );
    }

    return { arrayItemFields, fieldNode, hiddenValidationFields };
  }, [
    subschema,
    fields.value,
    index,
    allValues,
    getField,
    name,
    fieldName,
    arrayProps,
    collapsible,
    isCollapsed,
    getRequiredFields,
  ]);

  const handleCopy = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.stopPropagation();
    onCopy?.(item);
  };

  const handlePaste = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onPaste?.(index);
  };

  // final form может разбивать строку на символы
  if (typeof error == "object" && "0" in error) {
    error = Object.values(error).join("");
  }

  return (
    <Draggable
      key={name}
      draggableId={name}
      index={index}
      isDragDisabled={disableReorder}
    >
      {(provided: DraggableProvided, snapshot: DraggableStateSnapshot) => (
        <StyledPaper
          ref={provided.innerRef}
          {...provided.draggableProps}
          style={getItemStyle(provided.draggableProps.style || {})}
          elevation={snapshot.isDragging ? 4 : 1}
          $isDragging={snapshot.isDragging}
          $isDeleted={!!item?.deleted}
        >
          {/* Error */}
          {touched && typeof error == "string" && (
            <ErrorMessage error={{ message: error }} />
          )}

          {/* Header */}
          <Box
            className={`${classes.header} ${collapsible ? classes.headerCollapsible : ""} ${isCollapsed ? classes.headerCollapsed : ""}`}
            onClick={() => collapsible && toggleCollapse()}
            style={{ position: "relative" }}
            sx={
              shouldHighlight
                ? {
                    backgroundColor: "rgba(25, 118, 210, 0.2) !important", // 20% opacity primary color
                    "&:hover": {
                      backgroundColor: "rgba(25, 118, 210, 0.3) !important", // 30% opacity primary color on hover
                    },
                  }
                : {}
            }
          >
            {/* Drag Handle */}
            {!disableReorder && (
              <div
                {...provided.dragHandleProps}
                className={classes.dragHandle}
                onClick={(e) => e.stopPropagation()}
              >
                <DragIndicatorIcon color="action" />
              </div>
            )}
            {/* Title */}
            <Typography
              variant="subtitle1"
              className={`${classes.title} ${item?.deleted ? classes.titleDeleted : ""}`}
              sx={
                shouldHighlight
                  ? {
                      color: "primary.main",
                      fontWeight: "bold",
                    }
                  : {}
              }
            >
              {itemTitle}
            </Typography>
            {/* Hidden validation fields positioned near collapse icon */}
            {hiddenValidationFields}

            {showCopyPaste && (
              <>
                <Tooltip
                  title={t("shared.form.paste", { defaultValue: "Вставить" })}
                >
                  <IconButton
                    size="small"
                    style={{ marginRight: 8 }}
                    onClick={handlePaste}
                  >
                    <ContentPasteIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip
                  title={t("shared.form.copy", { defaultValue: "Копировать" })}
                >
                  <IconButton
                    size="small"
                    style={{ marginRight: 8 }}
                    onClick={handleCopy}
                  >
                    <ContentCopyIcon />
                  </IconButton>
                </Tooltip>
              </>
            )}

            {/* Collapse Icon */}
            {collapsible && (
              <IconButton size="small" style={{ marginRight: 8 }}>
                {isCollapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
              </IconButton>
            )}
            {/* Action Buttons */}
            {!hideRemoveButton && (
              <div onClick={(e) => e.stopPropagation()}>
                {item?.deleted ? (
                  <IconButton
                    onClick={() => {
                      fields.update(index, {
                        ...item,
                        deleted: false,
                      });
                    }}
                    title={restoreText}
                    size="small"
                    disabled={disableRemove}
                  >
                    <SettingsBackupRestoreIcon />
                  </IconButton>
                ) : (
                  <IconButton
                    onClick={() => {
                      if (softDelete && item._id) {
                        fields.update(index, {
                          ...item,
                          deleted: true,
                        });
                      } else {
                        fields.remove(index);
                      }
                    }}
                    title={removeText}
                    size="small"
                    disabled={disableRemove}
                  >
                    <ClearIcon />
                  </IconButton>
                )}
              </div>
            )}
          </Box>

          {/* Content */}
          <Collapse in={!isCollapsed} mountOnEnter unmountOnExit>
            <div className={classes.content}>
              <Grid container spacing={spacing}>
                {arrayItemFields || fieldNode}
              </Grid>
            </div>
          </Collapse>
        </StyledPaper>
      )}
    </Draggable>
  );
};
